// Operation-editing panel (#Update) rebuild — owns all rendering for the new
// desktop workbench / phone layout / hold / move / duplicate / overflow UI.
// Imported by app.js and called once per open/refresh from the end of
// opCheck() (see the linchpin note in the plan). Do not re-fetch data here —
// opCheck() already resolved `data`/`customerData` before calling in.
// Plan: C:\Users\machy\.claude\plans\wobbly-beaming-marshmallow.md

import { isPaintOp, paintLine, paintName, paintSwatch } from './bottomPaint';
import { patchHTML } from './liveView';
import {
  jobsByWorkOrder, opCheck, updateJobInfo, updateCustomerInfo,
  refreshWorkOrderRow, refreshOpRowSeverity, getCurrentUserData, currentURL,
  getOpenWorkOrdersForBoat, getCustomerRecord, getFormattedDate,
  addJobNote, editWorkOrderData, deleteOperationDM, updateWorkOrderDM,
  globalUserData, addOperation, shrinkwrapLabor,
} from './app';
import { toastMessage, fetchIdToken, idbGet, idbPut, generateId, isAdminRole, woLabel, isShrinkwrapInstall, notifyLocalDataChanged } from './utilities';
import { SHRINK_WIDTHS, estimateWrap, recordedWrap, fmtWrap, parseFeet } from './shrinkwrapCore';
import { patchBoatFields, patchJobFields, currentUserName } from './haulLaunchShared';
import { formatDateToYYYYMMDD } from './date-format';
import { renderOfficeChecklist } from './checklistOffice';
import { renderOpOrders } from './opOrders';

function escapeHtml(str) {
  return (str || '').toString()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Rail sibling-dot colors — a dedicated mapping per the design spec's exact
// rail-dot rule (teal closed/complete, amber in progress, gray not started,
// red on hold). Deliberately not reusing statusToneSlug()/OP_ROW_PILL_TONE:
// those serve other UI (the status <select>'s tone class, the WO-expand
// table's severity pill) and group statuses differently — e.g. they treat
// Closed and Hold both as "red"/"slate", which does not match this rail's
// 4-color spec.
function opRailDotColor(status) {
  if (status === 'Hold') return '#C23A3A';
  if (status === 'Closed' || status === 'Ready to Bill' || status === 'Complete') return '#0F8C82';
  if (status === 'In Progress') return '#DEA834';
  return '#C7D0DA';
}

// ---------- Stage 2: work-order operation switcher ----------

function renderRailSwitcher(data) {
  const container = document.getElementById('opRailWoSwitcher');
  if (!container) return;

  const workOrder = data.Work_Order;
  // Declined ops stay in jobsByWorkOrder (soft-deleted via Status only, see
  // deleteOperation() in app.js) — the main operations table explicitly
  // filters them out at render time (opTableList()'s `if (r.Status ===
  // 'Declined') return;`), but this rail read straight from the map without
  // an equivalent filter, so declined/soft-deleted ops kept showing up here
  // (e.g. "7/8" siblings when the work order only has 4 real operations).
  // Keeps `data` itself even if declined, so viewing a declined op's own
  // panel directly doesn't lose its rail highlight.
  const siblings = ((workOrder && jobsByWorkOrder && jobsByWorkOrder.get(workOrder)) || [data])
    .filter((op) => op.Status !== 'Declined' || op.Job_ID === data.Job_ID);
  const total = siblings.length;
  let currentIndex = siblings.findIndex((o) => o.Job_ID === data.Job_ID);
  if (currentIndex === -1) currentIndex = 0;

  const rowsHtml = siblings.map((op, i) => {
    const isCurrent = i === currentIndex;
    const dotColor = opRailDotColor(op.Status);
    // Short_Description is usually just the category ("Winterizing" on every op of a winter WO),
    // so name each op by what it is, like the Work Orders op rows do.
    const full = op.Short_Description_DM || op.Description || op.Short_Description || '';
    const name = full.split(/[:([]/)[0].trim() || op.Short_Description || 'Untitled operation';
    const title = `${op.OpCode ? `<span class="op-rail-sibling-code">${escapeHtml(op.OpCode)}</span>` : ''}${escapeHtml(name)}`;
    const held = op.Status === 'Hold';
    let holdLineHtml = '';
    if (held) {
      const parts = [];
      if (op.Hold_Reason) parts.push(escapeHtml(op.Hold_Reason));
      if (op.Hold_Follow_Up_Date) parts.push(`follow up ${formatMMDD(op.Hold_Follow_Up_Date)}`);
      holdLineHtml = `<span class="op-rail-sibling-hold-line"><span class="op-rail-sibling-hold-tag">Hold</span>${parts.length ? escapeHtml(parts.join(' · ')) : ''}</span>`;
    }
    return `
      <button type="button" class="op-rail-sibling-row${isCurrent ? ' op-rail-sibling-current' : ''}" data-sibling-index="${i}">
        <span class="op-rail-sibling-dot" style="background:${dotColor}"></span>
        <span class="op-rail-sibling-text">
          <span class="op-rail-sibling-title" title="${escapeHtml(full || name)}">${title}</span>
          ${holdLineHtml}
        </span>
        ${isCurrent ? `<span class="op-rail-sibling-count">${currentIndex + 1}/${total}</span>` : ''}
      </button>
    `;
  }).join('');

  patchHTML(container, `
    <div class="op-rail-wo-header">
      <span class="op-rail-wo-header-label">Work order ${escapeHtml(workOrder || '\u2014')}</span>
      <span class="op-rail-wo-nav">
        <button type="button" class="op-rail-wo-nav-btn" id="opRailPrevBtn" ${total <= 1 ? 'disabled' : ''}>&lsaquo;</button>
        <button type="button" class="op-rail-wo-nav-btn" id="opRailNextBtn" ${total <= 1 ? 'disabled' : ''}>&rsaquo;</button>
      </span>
    </div>
    <div class="op-rail-sibling-list">${rowsHtml}</div>
  `);

  const goToSibling = (index) => {
    const target = siblings[index];
    if (!target) return;
    opCheck(null, target);
  };

  container.querySelectorAll('.op-rail-sibling-row').forEach((row) => {
    row.onclick = () => {
      goToSibling(Number(row.getAttribute('data-sibling-index')));
    };
  });

  const prevBtn = document.getElementById('opRailPrevBtn');
  const nextBtn = document.getElementById('opRailNextBtn');
  if (prevBtn && total > 1) {
    prevBtn.onclick = () => goToSibling((currentIndex - 1 + total) % total);
  }
  if (nextBtn && total > 1) {
    nextBtn.onclick = () => goToSibling((currentIndex + 1) % total);
  }
}

// ---------- Stage 3: header + per-field blur/Enter autosave ----------
//
// Replaces the old generic DOMContentLoaded handler (app.js ~20028-20127)
// for every field that carries the new `op-field` class instead of
// `update-form` — that class rename is what excludes these fields from the
// old handler's one-time `.update-form` querySelectorAll, so there is no
// double-handling to guard against. The old handler's bug was a single
// `debounceTimer` shared across every field (editing field A then B within
// 2s dropped A's save); this rewrite has no shared timer at all — it commits
// on blur or Enter, per field, matching the design spec's stated intent.

function opFieldWrap(el) {
  return el.closest('.op-field-wrap');
}

function setFieldState(wrap, state) {
  if (!wrap) return;
  wrap.classList.remove('is-focused', 'is-saved', 'is-error');
  if (state) wrap.classList.add(state);
}

function formatTimeShort(date) {
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
}

function setSavedIndicator(text, isError) {
  const el = document.getElementById('opSavedIndicator');
  if (!el) return;
  el.textContent = text;
  el.classList.toggle('is-error', !!isError);
}

// Copied verbatim (structure, not line-for-line) from the old generic
// autosave handler, app.js ~20053-20099 — patches the same 5 in-DOM caches
// so any other open view (haul/spring iframes, Operations-tab rows,
// WO-expand rows) reflects the edit live without a refetch. Do not drop
// this when refactoring; it is silently load-bearing for those other views.
function patchLiveCaches(jobId, jobIndex, value) {
  if (!jobId || !jobIndex) return;

  const haulingIframe = document.querySelector('#hauling iframe');
  if (haulingIframe) {
    const iframeDocument = haulingIframe.contentDocument || haulingIframe.contentWindow.document;
    const row = iframeDocument.querySelector(`.haul-item[data-id="${jobId}"]`);
    if (row) {
      const opData = JSON.parse(row.getAttribute('data-jobInfo'));
      opData[jobIndex] = value;
      row.setAttribute('data-jobInfo', JSON.stringify(opData));
    }
  }

  const launchingIframe = document.querySelector('#spring iframe');
  if (launchingIframe) {
    const iframeDocument = launchingIframe.contentDocument || launchingIframe.contentWindow.document;
    const row = iframeDocument.querySelector(`.launch-item[data-id="${jobId}"]`);
    if (row) {
      const opData = JSON.parse(row.getAttribute('data-jobInfo'));
      opData[jobIndex] = value;
      row.setAttribute('data-jobInfo', JSON.stringify(opData));
    }
  }

  const jobItemRow = document.querySelector(`.job-item[data-id="${jobId}"]`);
  if (jobItemRow) {
    const opData = JSON.parse(jobItemRow.getAttribute('data-jobInfo'));
    opData[jobIndex] = value;
    jobItemRow.setAttribute('data-jobInfo', JSON.stringify(opData));
  }

  const haulItemRow = document.querySelector(`.haul-item[data-id="${jobId}"]`);
  if (haulItemRow) {
    const opData = JSON.parse(haulItemRow.getAttribute('data-jobInfo'));
    opData[jobIndex] = value;
    haulItemRow.setAttribute('data-jobInfo', JSON.stringify(opData));
  }

  const activeWorkOrderEl = document.querySelector('.active-work-order');
  if (activeWorkOrderEl) {
    const woData = JSON.parse(activeWorkOrderEl.getAttribute('data-jobInfo'));
    woData[jobIndex] = value;
    activeWorkOrderEl.setAttribute('data-jobInfo', JSON.stringify(woData));
  }

  const opTableRow = document.querySelector(`.op-table-data-row[data-id="${jobId}"]`);
  if (opTableRow) {
    const woOpData = JSON.parse(opTableRow.getAttribute('data-jobInfo'));
    woOpData[jobIndex] = value;
    opTableRow.setAttribute('data-jobInfo', JSON.stringify(woOpData));
  }
}

async function commitField(el) {
  const wrap = opFieldWrap(el);
  const value = el.value;

  if (el.dataset.opFieldLastCommitted === value) return; // no change — don't write or flash state

  const jobId = document.getElementById('currentJobId')?.value;
  const customerId = document.getElementById('updateCustomerId')?.value;
  const jobIndex = el.getAttribute('data-update');
  const customerIndex = el.getAttribute('data-update-customer');

  setSavedIndicator('Saving…', false);
  patchLiveCaches(jobId, jobIndex, value);

  try {
    if (jobIndex) {
      await updateJobInfo(jobId, jobIndex, value, { strict: true });
    } else if (customerIndex) {
      await updateCustomerInfo(customerId, customerIndex, value);
    }
    el.dataset.opFieldLastCommitted = value;
    setFieldState(wrap, 'is-saved');
    setSavedIndicator(`Saved ${formatTimeShort(new Date())}`, false);
    setTimeout(() => {
      if (wrap && wrap.classList.contains('is-saved')) setFieldState(wrap, null);
    }, 1200);
  } catch (error) {
    console.error('[opPanel] Error updating field:', error);
    setFieldState(wrap, 'is-error');
    setSavedIndicator('Unsaved changes', true);
    toastMessage('Failed to update info. Please try again.', 'red');
  }
}

let fieldAutosaveInitialized = false;

function initFieldAutosave() {
  if (fieldAutosaveInitialized) return;
  fieldAutosaveInitialized = true;

  const panel = document.getElementById('Update');
  if (!panel) return;

  panel.addEventListener('focusin', (e) => {
    const el = e.target;
    if (!el.classList || !el.classList.contains('op-field')) return;
    if (el.dataset.opFieldLastCommitted === undefined) el.dataset.opFieldLastCommitted = el.value;
    setFieldState(opFieldWrap(el), 'is-focused');
  });

  panel.addEventListener('focusout', (e) => {
    const el = e.target;
    if (!el.classList || !el.classList.contains('op-field')) return;
    commitField(el);
  });

  panel.addEventListener('keydown', (e) => {
    const el = e.target;
    if (!el.classList || !el.classList.contains('op-field')) return;
    if (e.key === 'Enter' && el.tagName !== 'TEXTAREA') {
      e.preventDefault();
      el.blur(); // triggers focusout -> commitField
    }
  });
}

// A field's baseline "last committed value" must be reset whenever
// renderOpPanel() repopulates the panel for a different operation (rail
// switch, follow-up "Open op", etc.) — otherwise a stale baseline from the
// previous operation could suppress a legitimate first save on the new one,
// or flash a false "saved" state for a value the user never touched here.
function resetFieldBaselines() {
  document.querySelectorAll('#Update .op-field').forEach((el) => {
    el.dataset.opFieldLastCommitted = el.value;
    setFieldState(opFieldWrap(el), null);
  });
}

// ---------- Generic popover menu (Stage 4 status "More", Stage 6 overflow) ----------

let activePopover = null;

function closeActivePopover() {
  if (activePopover) {
    activePopover.remove();
    activePopover = null;
  }
}

// items: [{ label, onClick, danger, disabled, divider }]
function opOpenMenu(anchorEl, items) {
  closeActivePopover();
  const rect = anchorEl.getBoundingClientRect();
  const menu = document.createElement('div');
  menu.className = 'op-popover';
  menu.style.right = `${window.innerWidth - rect.right}px`;

  // Estimate menu height (item row ~34px, divider ~11px, container padding
  // 14px) to decide whether it fits below the anchor or needs to open
  // upward instead — the footer's "More" button sits near the bottom of a
  // fixed-height, viewport-centered modal, so "always open downward" (fine
  // for the status strip's "More", near the top) clips off-screen here.
  const estimatedHeight = items.reduce((h, item) => h + (item.divider ? 11 : 34), 14);
  const spaceBelow = window.innerHeight - rect.bottom - 6;
  if (spaceBelow < estimatedHeight) {
    menu.style.bottom = `${window.innerHeight - rect.top + 6}px`;
  } else {
    menu.style.top = `${rect.bottom + 6}px`;
  }

  items.forEach((item) => {
    if (item.divider) {
      const div = document.createElement('div');
      div.className = 'op-popover-divider';
      menu.appendChild(div);
      return;
    }
    const row = document.createElement('div');
    row.className = 'op-popover-item'
      + (item.danger ? ' op-popover-item--danger' : '')
      + (item.disabled ? ' op-popover-item--disabled' : '');
    row.textContent = item.label;
    if (!item.disabled) {
      row.addEventListener('click', (e) => {
        e.stopPropagation();
        closeActivePopover();
        item.onClick();
      });
    }
    menu.appendChild(row);
  });

  document.body.appendChild(menu);
  activePopover = menu;
  // Next tick so the click that opened this menu doesn't immediately close it.
  setTimeout(() => document.addEventListener('click', closeActivePopover, { once: true }), 0);
}

// ---------- Generic dialog shell (Stage 4 placeholders; Stages 7-9 build the
// real Hold / Decline / Move dialogs on top of this same scrim+card chrome) ----------

function opCloseDialog() {
  const scrim = document.getElementById('opDialogScrim');
  if (scrim) scrim.remove();
}

function opOpenDialog({ title, subtitle, bodyHtml, footerHtml, widthPx }) {
  opCloseDialog();
  const scrim = document.createElement('div');
  scrim.className = 'op-dialog-scrim';
  scrim.id = 'opDialogScrim';
  scrim.innerHTML = `
    <div class="op-dialog" style="width:${widthPx || 440}px">
      <div class="op-dialog-head">
        <div class="op-dialog-title">${title}</div>
        ${subtitle ? `<div class="op-dialog-sub">${subtitle}</div>` : ''}
      </div>
      <div class="op-dialog-body">${bodyHtml || ''}</div>
      <div class="op-dialog-foot">${footerHtml || ''}</div>
    </div>
  `;
  scrim.addEventListener('click', (e) => {
    if (e.target === scrim) opCloseDialog();
  });
  document.body.appendChild(scrim);
  return scrim;
}

// ---------- Stage 4: status strip (5 pills + More popover) ----------

const STATUS_PRIMARY_PILLS = [
  { label: 'On schedule', value: 'On Work Schedule' },
  { label: 'In progress', value: 'In Progress' },
  { label: 'Ready to bill', value: 'Ready to Bill' },
  { label: 'Closed', value: 'Closed' },
];

// A dedicated 4-tone mapping matching the design spec's exact selected-pill
// rule. Deliberately not statusToneSlug() (app.js) — that helper serves the
// legacy <select>'s tone class and uses a different, incompatible scheme
// (e.g. Closed→red, Hold→slate there, vs. Closed→teal, Hold→red here); reusing
// it would visibly contradict this component's "final and exact" spec.
function opStatusPillTone(status) {
  if (status === 'Closed' || status === 'Ready to Bill' || status === 'Complete') return 'teal';
  if (status === 'In Progress') return 'amber';
  if (status === 'Hold' || status === 'Declined') return 'red';
  return 'slate';
}

const STATUS_TONE_HEX = {
  teal: { bg: '#0F8C82', border: '#0E7C73' },
  amber: { bg: '#DEA834', border: '#D19E28' },
  red: { bg: '#C23A3A', border: '#B03333' },
  slate: { bg: '#5A6B7B', border: '#4E5E6D' },
};

function patchJobsByWorkOrderCache(workOrder, jobId, patch) {
  if (!jobsByWorkOrder || !workOrder) return;
  const arr = jobsByWorkOrder.get(workOrder);
  if (!arr) return;
  const entry = arr.find((o) => o.Job_ID === jobId);
  if (entry) Object.assign(entry, patch);
}

// Every status-strip write goes through opCheck(null, {...}) rather than
// hand-patching the panel's own fields — this reuses the exact same
// populate/side-effect path Stage 2's sibling switcher already relies on
// (context strip, hidden fields, tone class, shrinkwrap-menu visibility,
// etc. all stay correct for free) instead of re-deriving a partial patch.
async function commitStatus(data, value) {
  try {
    await updateJobInfo(data.Job_ID, 'Status', value, { strict: true });
    patchJobsByWorkOrderCache(data.Work_Order, data.Job_ID, { Status: value });
    opCheck(null, { ...data, Status: value });
  } catch (error) {
    console.error('[opPanel] Error updating status:', error);
    toastMessage('Failed to update status. Please try again.', 'red');
  }
}

// ---------- Stage 7a: Hold with a reason ----------

const HOLD_REASONS = ['Awaiting owner approval', 'Awaiting parts', 'Weather', 'Boat inaccessible', 'Billing', 'Other…'];
const HOLD_FIELDS = ['Previous_Status', 'Hold_Reason', 'Hold_Detail', 'Hold_Follow_Up_Date', 'Hold_Started_At', 'Hold_Set_By'];

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

async function patchJobRTDB(jobId, updates) {
  const idToken = await fetchIdToken();
  const jobUrl = `${currentURL}/data/${jobId}.json?auth=${idToken}`;
  const lastUpdatedUrl = `${currentURL}/data/lastUpdated.json?auth=${idToken}`;
  const res = await fetch(jobUrl, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) });
  // Callers update the rows as if it saved — a rejected write must not look like a hold/release.
  if (!res.ok) throw new Error(`Saving the operation failed (HTTP ${res.status})`);
  await fetch(lastUpdatedUrl, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ '.sv': 'timestamp' }) });

  // Mirror to IndexedDB the same way deleteOperation() does (app.js
  // ~11318-11321) — read-merge-write on the 'jobData' store, best-effort.
  try {
    const existing = await idbGet('jobData', jobId);
    if (existing) {
      const merged = { ...existing };
      Object.entries(updates).forEach(([k, v]) => {
        if (v === null) delete merged[k];
        else merged[k] = v;
      });
      await idbPut('jobData', merged);
    }
  } catch (_) { /* best-effort, matches deleteOperation()'s own swallow */ }
}

// The operation as the server has it now (null offline / not found). Hold, release and move act on
// this, not the panel's copy: someone may have changed the operation since it was opened here.
async function serverOp(jobId) {
  try {
    const idToken = await fetchIdToken();
    const res = await fetch(`${currentURL}/data/${jobId}.json?auth=${idToken}`);
    const v = res.ok ? await res.json() : null;
    return v && typeof v === 'object' ? { ...v, Job_ID: jobId, id: jobId } : null;
  } catch (_) { return null; }
}

function refreshRowsAfterMutation(workOrder, jobId, freshData) {
  patchJobsByWorkOrderCache(workOrder, jobId, freshData);
  const row = document.querySelector(`.op-table-data-row[data-id="${jobId}"]`);
  if (row) refreshOpRowSeverity(row, freshData);
  refreshWorkOrderRow(workOrder);
}

// Replaces holdOperation() (app.js) as the real write path. Bug fix vs. the
// original: snapshots Previous_Status from the op's ACTUAL current status
// (not assumed) before overwriting, so releaseHoldOperation() can restore it
// correctly instead of hardcoding "On Work Schedule" regardless of history.
async function holdOperationWithReason(panelData, { reason, detail, followUpDate }) {
  const data = (await serverOp(panelData.Job_ID)) || panelData;
  if (data.Status === 'Hold') {
    // Someone else held it a moment ago. Holding again would save "Hold" as the status to return
    // to, and releasing would then leave it stuck on hold.
    toastMessage(`${data.Hold_Set_By || 'Someone else'} just put this operation on hold.`, 'blue', 4000);
    if (document.getElementById('currentJobId')?.value === data.Job_ID) opCheck(null, data);
    return false;
  }
  const user = await getCurrentUserData();
  const updates = {
    Status: 'Hold',
    Previous_Status: data.Status,
    Hold_Reason: reason,
    Hold_Detail: detail || null,
    Hold_Follow_Up_Date: followUpDate || null,
    Hold_Started_At: new Date().toISOString(),
    Hold_Set_By: (user && (user.displayName || user.email)) || 'Unknown',
  };
  await patchJobRTDB(data.Job_ID, updates);
  const freshData = { ...data, ...updates };
  refreshRowsAfterMutation(data.Work_Order, data.Job_ID, freshData);
  if (document.getElementById('currentJobId')?.value === data.Job_ID) {
    opCheck(null, freshData);
  }
}

// Replaces removeHoldOperation() (app.js). Accepts an explicit jobId/data
// pair (not just #currentJobId) because Stage 11's follow-up prompt needs to
// release holds for ops other than whichever one is currently open in
// #Update.
async function releaseHoldOperation(panelData) {
  const data = (await serverOp(panelData.Job_ID)) || panelData;
  if (data.Status !== 'Hold') {
    // Already released (or moved on) by someone else: restoring the old status would undo them.
    toastMessage('Someone else already took this operation off hold.', 'blue', 4000);
    refreshRowsAfterMutation(data.Work_Order, data.Job_ID, data);
    if (document.getElementById('currentJobId')?.value === data.Job_ID) opCheck(null, data);
    return false;
  }
  const restoredStatus = data.Previous_Status || 'On Work Schedule';
  const updates = { Status: restoredStatus };
  HOLD_FIELDS.forEach((k) => { updates[k] = null; });

  await patchJobRTDB(data.Job_ID, updates);
  const freshData = { ...data };
  HOLD_FIELDS.forEach((k) => delete freshData[k]);
  freshData.Status = restoredStatus;
  refreshRowsAfterMutation(data.Work_Order, data.Job_ID, freshData);
  if (document.getElementById('currentJobId')?.value === data.Job_ID) {
    opCheck(null, freshData);
  }
}

function releaseHoldWithToast(data) {
  return releaseHoldOperation(data).catch((error) => {
    console.error('[opPanel] Error releasing hold:', error);
    toastMessage('Failed to release hold. Please try again.', 'red');
  });
}

function openHoldDialog(data) {
  const in3Days = addDays(new Date(), 3);
  const in3DaysIso = toISODate(in3Days);
  const in3DaysLabel = `${String(in3Days.getMonth() + 1).padStart(2, '0')}/${String(in3Days.getDate()).padStart(2, '0')}`;

  const state = { reason: null, followUp: 'in3days' };

  const bodyHtml = `
    <div class="op-hold-field">
      <div class="op-rail-fact-label">Reason</div>
      <div class="op-chip-row" id="opHoldReasonChips">
        ${HOLD_REASONS.map((r) => `<button type="button" class="op-chip" data-reason="${escapeHtml(r)}">${escapeHtml(r)}</button>`).join('')}
      </div>
    </div>
    <div class="op-hold-field">
      <div class="op-hold-detail-label-row">
        <span class="op-rail-fact-label">Detail</span>
        <span class="op-notes-hint" id="opHoldDetailHint">optional · one line, shown on the row</span>
      </div>
      <div class="op-field-wrap"><input type="text" id="opHoldDetailInput" class="op-field" autocomplete="off" /></div>
    </div>
    <div class="op-hold-field">
      <div class="op-rail-fact-label">Follow up</div>
      <div class="op-chip-row" id="opHoldFollowUpChips">
        <button type="button" class="op-chip" data-followup="none">No date</button>
        <button type="button" class="op-chip op-chip--selected-teal" data-followup="in3days">In 3 days · ${in3DaysLabel}</button>
        <button type="button" class="op-chip" data-followup="pick">Pick a date</button>
      </div>
      <div class="op-field-wrap" id="opHoldDatePickerWrap" style="display:none;margin-top:8px;">
        <input type="date" id="opHoldDateInput" class="op-field" value="${in3DaysIso}" />
      </div>
    </div>
  `;

  const footerHtml = `
    <span class="op-dialog-hint">Status becomes Hold</span>
    <button type="button" class="op-btn-secondary" data-dialog-cancel>Cancel</button>
    <button type="button" class="op-btn-primary op-btn-primary--danger op-disabled" id="opHoldSubmitBtn" disabled>Hold operation</button>
  `;

  const scrim = opOpenDialog({
    title: 'Put this operation on hold',
    subtitle: 'The reason shows on the op row, so whoever walks past the boat knows why it’s parked.',
    bodyHtml,
    footerHtml,
    widthPx: 440,
  });

  scrim.querySelector('[data-dialog-cancel]').addEventListener('click', opCloseDialog);

  const submitBtn = document.getElementById('opHoldSubmitBtn');
  const detailInput = document.getElementById('opHoldDetailInput');
  const detailHint = document.getElementById('opHoldDetailHint');
  const datePickerWrap = document.getElementById('opHoldDatePickerWrap');
  const dateInput = document.getElementById('opHoldDateInput');

  function updateSubmitEnabled() {
    const isOther = state.reason === 'Other…';
    const reasonOk = !!state.reason && (!isOther || detailInput.value.trim().length > 0);
    submitBtn.disabled = !reasonOk;
    submitBtn.classList.toggle('op-disabled', !reasonOk);
  }

  scrim.querySelectorAll('#opHoldReasonChips .op-chip').forEach((chip) => {
    chip.addEventListener('click', () => {
      state.reason = chip.getAttribute('data-reason');
      scrim.querySelectorAll('#opHoldReasonChips .op-chip').forEach((c) => c.classList.toggle('op-chip--selected-red', c === chip));
      detailHint.textContent = state.reason === 'Other…' ? 'required for Other…' : 'optional · one line, shown on the row';
      updateSubmitEnabled();
    });
  });

  detailInput.addEventListener('input', updateSubmitEnabled);

  scrim.querySelectorAll('#opHoldFollowUpChips .op-chip').forEach((chip) => {
    chip.addEventListener('click', () => {
      state.followUp = chip.getAttribute('data-followup');
      scrim.querySelectorAll('#opHoldFollowUpChips .op-chip').forEach((c) => c.classList.toggle('op-chip--selected-teal', c === chip));
      datePickerWrap.style.display = state.followUp === 'pick' ? 'flex' : 'none';
    });
  });

  submitBtn.addEventListener('click', async () => {
    if (submitBtn.disabled) return;
    let followUpDate = null;
    if (state.followUp === 'in3days') followUpDate = in3DaysIso;
    else if (state.followUp === 'pick') followUpDate = dateInput.value || null;

    submitBtn.disabled = true;
    submitBtn.textContent = 'Holding…';
    try {
      const held = await holdOperationWithReason(data, { reason: state.reason, detail: detailInput.value.trim(), followUpDate });
      opCloseDialog();
      if (held !== false) toastMessage('Operation put on hold', 'green');
    } catch (error) {
      console.error('[opPanel] Error holding operation:', error);
      toastMessage('Failed to put operation on hold. Please try again.', 'red');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Hold operation';
    }
  });
}

// ---------- Stage 8: Decline operation (dialog consolidation) ----------
//
// Replaces the two sequential native popups (showConfirmationPopup() then
// showDeclineNotePopup(), app.js) with one dialog. Exported so app.js's
// deleteOperation() can call it in place of those two calls — everything
// below that point in deleteOperation() (the Declined_Note/Declined_User/
// Declined_Date/DM_Delete_Pending PATCH, the Dockmaster sync attempt, the
// soft-failure toast) stays byte-for-byte unchanged; only how the
// confirmation + reason get collected changes.
export function openDeclineConfirmDialog() {
  return new Promise((resolve) => {
    const bodyHtml = `
      <div class="op-hold-field">
        <div class="op-hold-detail-label-row">
          <span class="op-rail-fact-label">Reason</span>
          <span class="op-notes-hint">required · recorded on the operation</span>
        </div>
        <div class="op-field-wrap"><input type="text" id="opDeclineNoteInput" class="op-field" autocomplete="off" /></div>
      </div>
    `;
    const footerHtml = `
      <button type="button" class="op-btn-secondary" data-dialog-cancel>Cancel</button>
      <button type="button" class="op-btn-primary op-btn-primary--danger op-disabled" id="opDeclineSubmitBtn" disabled>Decline operation</button>
    `;

    const scrim = opOpenDialog({
      title: 'Decline this operation?',
      subtitle: 'This records a reason and syncs the decline to Dockmaster.',
      bodyHtml,
      footerHtml,
      widthPx: 440,
    });

    let settled = false;
    const settle = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    scrim.querySelector('[data-dialog-cancel]').addEventListener('click', () => {
      opCloseDialog();
      settle(null);
    });

    const noteInput = document.getElementById('opDeclineNoteInput');
    const submitBtn = document.getElementById('opDeclineSubmitBtn');
    noteInput.addEventListener('input', () => {
      const ok = noteInput.value.trim().length > 0;
      submitBtn.disabled = !ok;
      submitBtn.classList.toggle('op-disabled', !ok);
    });
    submitBtn.addEventListener('click', () => {
      const note = noteInput.value.trim();
      if (!note) return;
      opCloseDialog();
      settle({ confirmed: true, note });
    });

    // Closing via the scrim click (opOpenDialog's own outside-click handler)
    // also needs to resolve the promise, not leave deleteOperation() hanging.
    scrim.addEventListener('click', (e) => {
      if (e.target === scrim) settle(null);
    });
  });
}

// ---------- Stage 13: shrinkwrap stage track ----------
//
// "Ready to Frame" is the implicit starting point (never a real write —
// shrinkwrapLabor() has no case for it) and "Awaiting Review" is the
// terminal state shrinkwrapLabor() jumps straight to when "Vents Installed"
// is marked done (it never actually writes Status: 'Vents Installed' as an
// intermediate value — confirmed by reading shrinkwrapLabor() directly).
// Both are still listed as track rows per the design spec; only the 5
// middle stages are ever real "Mark done" targets.
const SHRINK_STAGES = ['Ready to Frame', 'Framed', 'Vacuumed', 'Covered', 'Tightened', 'Vents Installed', 'Awaiting Review'];

// shrinkwrapLabor() (app.js) only updates the legacy hidden #updateStatus
// select — it doesn't know about renderOpPanel(), so (same pattern as the
// old holdOperation()/removeHoldOperation() before Stage 7 rewired them)
// calling it alone leaves this panel showing stale track state. Refresh via
// opCheck() afterward using the status shrinkwrapLabor() actually wrote,
// mirrored from its own status-per-stage mapping (app.js) rather than
// re-reading the DOM select.
const SHRINK_STAGE_TO_STATUS = {
  Framed: 'Framed',
  Vacuumed: 'Vacuumed',
  Covered: 'Covered',
  Tightened: 'Tightened',
  'Vents Installed': 'Awaiting Review',
};

function openShrinkwrapHoursDialog(data, stageName) {
  // Covering is when the film is cut, so that step also records the size used (saved to the boat for next time).
  const askSize = stageName === 'Covered';
  const bodyHtml = `
    <div class="op-hold-field">
      <div class="op-rail-fact-label">Hours for "${escapeHtml(stageName)}"</div>
      <div class="op-field-wrap"><input type="number" step="0.25" min="0" id="opShrinkHoursInput" class="op-field" autocomplete="off" /></div>
    </div>
    ${askSize ? `<div class="op-hold-field">
      <div class="op-rail-fact-label">Shrinkwrap used (width × length, ft)</div>
      ${wrapSizeInputsHTML('opShrinkDlg', wrapPrefill(data))}
    </div>` : ''}
  `;
  const footerHtml = `
    <button type="button" class="op-btn-secondary" data-dialog-cancel>Cancel</button>
    <button type="button" class="op-btn-primary" id="opShrinkHoursSubmit">Mark done</button>
  `;
  const scrim = opOpenDialog({ title: `Mark "${escapeHtml(stageName)}" done`, bodyHtml, footerHtml, widthPx: 360 });
  scrim.querySelector('[data-dialog-cancel]').addEventListener('click', opCloseDialog);

  const input = document.getElementById('opShrinkHoursInput');
  input.focus();
  const submit = async () => {
    const hours = input.value;
    if (!hours || Number(hours) <= 0) return;
    try {
      if (askSize) {
        const size = readWrapSizeInputs('opShrinkDlg');
        if (size) await saveWrapSize(data, size);
      }
      await shrinkwrapLabor(data.Job_ID, stageName, hours);
      opCloseDialog();
      const newStatus = SHRINK_STAGE_TO_STATUS[stageName];
      if (newStatus) opCheck(null, { ...data, Status: newStatus });
    } catch (error) {
      console.error('[opPanel] Error submitting shrinkwrap stage:', error);
      toastMessage('Failed to submit stage. Please try again.', 'red');
    }
  };
  document.getElementById('opShrinkHoursSubmit').addEventListener('click', submit);
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } });
}

// ---------- wrap size (shrinkwrapCore.js) ----------
// The boat keeps the size used last time (Shrinkwrap_Width/Length/Date/By/WO) so next season's
// techs can cut ahead; the op keeps the size used on this job. The estimate is LOA + 10 ft by
// 2 x beam + 4 ft, rounded up to a stock width.
let currentBoat = null;

function wrapPrefill(data) {
  const own = { width: parseFeet(data.Shrinkwrap_Width), length: parseFeet(data.Shrinkwrap_Length) };
  if (own.width && own.length) return own;
  return recordedWrap(currentBoat) || estimateWrap(currentBoat) || { width: null, length: null };
}

function wrapSizeInputsHTML(prefix, pre) {
  const widths = [...new Set([...SHRINK_WIDTHS, ...(pre.width ? [pre.width] : [])])].sort((a, b) => a - b);
  return `
    <div class="op-wrap-inputs">
      <div class="op-field-wrap op-field-wrap--select"><select id="${prefix}Width" class="op-field" aria-label="Width (ft)">
        <option value="">Width</option>
        ${widths.map(w => `<option value="${w}"${w === pre.width ? ' selected' : ''}>${w}'</option>`).join('')}
      </select></div>
      <span class="op-wrap-x">×</span>
      <div class="op-field-wrap"><input type="number" min="1" step="1" id="${prefix}Length" class="op-field" placeholder="Length" aria-label="Length (ft)" value="${pre.length || ''}" autocomplete="off" /></div>
      <span class="op-wrap-ft">ft</span>
    </div>`;
}

function readWrapSizeInputs(prefix) {
  const width = parseFeet(document.getElementById(`${prefix}Width`)?.value);
  const length = parseFeet(document.getElementById(`${prefix}Length`)?.value);
  return width && length ? { width, length } : null;
}

async function saveWrapSize(data, { width, length }) {
  const jobId = data.id || data.Job_ID;
  const boatFields = {
    Shrinkwrap_Width: width, Shrinkwrap_Length: length,
    Shrinkwrap_Date: formatDateToYYYYMMDD(new Date()), Shrinkwrap_By: currentUserName(), Shrinkwrap_WO: data.Work_Order || '',
  };
  if (data.Customer_ID && data.Boat_ID) {
    await patchBoatFields({ customerId: data.Customer_ID, boatId: data.Boat_ID }, boatFields);
    if (currentBoat) Object.assign(currentBoat, boatFields);
    notifyLocalDataChanged('customerData');
  }
  if (jobId) {
    await patchJobFields(jobId, { Shrinkwrap_Width: width, Shrinkwrap_Length: length });
    notifyLocalDataChanged('jobData');
  }
  if (currentOpData && (currentOpData.id || currentOpData.Job_ID) === jobId) {
    currentOpData = { ...currentOpData, Shrinkwrap_Width: width, Shrinkwrap_Length: length };
  }
}

function wrapSizeHTML(data) {
  const rec = recordedWrap(currentBoat);
  const est = estimateWrap(currentBoat);
  const own = { width: parseFeet(data.Shrinkwrap_Width), length: parseFeet(data.Shrinkwrap_Length) };
  const hasOwn = own.width && own.length;
  // Once this job's size is saved, the boat's record is this job's; don't call it "last time".
  const recIsThisJob = hasOwn && rec && rec.workOrder === (data.Work_Order || '') && rec.width === own.width && rec.length === own.length;
  const fact = (label, size, sub, cls = '') => `
    <div class="op-wrap-fact ${cls}">
      <span class="op-rail-fact-label">${label}</span>
      <span class="op-wrap-fact-size">${size ? escapeHtml(size) : '—'}</span>
      ${sub ? `<span class="op-wrap-fact-sub">${escapeHtml(sub)}</span>` : ''}
    </div>`;
  const recSub = rec ? [rec.date ? rec.date.slice(0, 4) : '', rec.workOrder ? woLabel(rec.workOrder) : '', rec.by].filter(Boolean).join(' · ') : 'Not recorded yet';
  const estSub = est ? `${est.loa}' LOA × ${est.beam}' beam${est.over ? " · over 40' wide, seam two pieces" : ''}` : 'Add length and beam to the boat';
  return `
    <div class="op-wrap-size">
      <div class="op-wrap-size-head">Shrinkwrap size</div>
      <div class="op-wrap-facts">
        ${hasOwn ? fact('Used on this job', fmtWrap(own), '', 'op-wrap-fact--own') : ''}
        ${recIsThisJob ? '' : fact('Used last time', rec ? fmtWrap(rec) : '', recSub, rec ? 'op-wrap-fact--recorded' : '')}
        ${fact('Estimate', est ? fmtWrap(est) : '', estSub)}
      </div>
      <div class="op-wrap-form">
        <span class="op-rail-fact-label">${hasOwn ? 'Change size used' : 'Size used'}</span>
        ${wrapSizeInputsHTML('opWrap', wrapPrefill(data))}
        <button type="button" class="op-btn-secondary op-wrap-save" id="opWrapSave">Save size</button>
      </div>
    </div>`;
}

function renderShrinkwrapTrack(container, data) {
  let currentIndex = SHRINK_STAGES.indexOf(data.Status);
  if (currentIndex === -1) currentIndex = 0;

  patchHTML(container, `
    <div class="op-shrink-track">
      ${SHRINK_STAGES.map((stage, i) => {
        if (i <= currentIndex) {
          return `
            <div class="op-shrink-row op-shrink-row--done">
              <span class="op-shrink-circle op-shrink-circle--done">&#10003;</span>
              <span class="op-shrink-label op-shrink-label--done">${escapeHtml(stage)}</span>
            </div>
          `;
        }
        if (i === currentIndex + 1) {
          return `
            <div class="op-shrink-row op-shrink-row--current">
              <span class="op-shrink-circle op-shrink-circle--current"></span>
              <span class="op-shrink-label op-shrink-label--current">${escapeHtml(stage)}</span>
              <button type="button" class="op-shrink-mark-done" data-stage="${escapeHtml(stage)}">Mark done</button>
            </div>
          `;
        }
        return `
          <div class="op-shrink-row op-shrink-row--upcoming">
            <span class="op-shrink-circle op-shrink-circle--upcoming"></span>
            <span class="op-shrink-label op-shrink-label--upcoming">${escapeHtml(stage)}</span>
          </div>
        `;
      }).join('')}
    </div>
    ${wrapSizeHTML(data)}
  `);

  const markBtn = container.querySelector('.op-shrink-mark-done');
  if (markBtn) {
    markBtn.onclick = () => openShrinkwrapHoursDialog(data, markBtn.getAttribute('data-stage'));
  }
  const saveBtn = container.querySelector('#opWrapSave');
  if (saveBtn) {
    saveBtn.onclick = async () => {
      const size = readWrapSizeInputs('opWrap');
      if (!size) { toastMessage('Pick a width and enter the length in feet', 'red'); return; }
      saveBtn.disabled = true;
      try {
        await saveWrapSize(data, size);
        toastMessage(`Saved ${fmtWrap(size)} to ${data.Boat_Name || 'the boat'}`, 'green');
        renderShrinkwrapTrack(container, currentOpData || data);
      } catch (error) {
        console.error('[opPanel] Error saving wrap size:', error);
        toastMessage('Could not save the size. Please try again.', 'red');
        saveBtn.disabled = false;
      }
    };
  }
}

function renderStatusStrip(data) {
  const container = document.getElementById('opStatusStrip');
  if (!container) return;

  const currentStatus = data.Status;

  if (isShrinkwrapInstall(data)) {
    // Shown at both breakpoints per decision #2 — no desktop-only fallback.
    renderShrinkwrapTrack(container, data);
    return;
  }

  const held = currentStatus === 'Hold';

  const pillsHtml = STATUS_PRIMARY_PILLS.map((p) => {
    const selected = !held && p.value === currentStatus;
    let style = '';
    if (selected) {
      const tone = STATUS_TONE_HEX[opStatusPillTone(p.value)];
      style = `background:${tone.bg};border-color:${tone.border};`;
    }
    return `
      <button type="button" class="op-status-pill${selected ? ' op-status-pill--selected' : ''}${held ? ' op-status-pill--disabled' : ''}" data-status-value="${escapeHtml(p.value)}" style="${style}" ${held ? 'disabled' : ''}>
        ${selected ? '<span class="op-status-pill-dot"></span>' : ''}${escapeHtml(p.label)}
      </button>
    `;
  }).join('');

  patchHTML(container, `
    <span class="op-status-strip-label">Status</span>
    ${pillsHtml}
    <button type="button" class="op-status-pill op-status-more-btn${held ? ' op-status-pill--disabled' : ''}" id="opStatusMoreBtn" ${held ? 'disabled' : ''}>More &#9662;</button>
    ${held ? '<span class="op-status-lock-note">locked while on hold</span>' : ''}
  `);

  if (held) return; // changing status requires releasing the hold first — no pill/More handlers while held

  container.querySelectorAll('.op-status-pill[data-status-value]').forEach((btn) => {
    btn.onclick = () => commitStatus(data, btn.getAttribute('data-status-value'));
  });

  const moreBtn = document.getElementById('opStatusMoreBtn');
  if (moreBtn) {
    moreBtn.onclick = () => {
      opOpenMenu(moreBtn, [
        { label: 'Estimate', onClick: () => commitStatus(data, 'Estimate') },
        { label: 'Awaiting Approval', onClick: () => commitStatus(data, 'Awaiting Approval') },
        { label: 'Hold', onClick: () => openHoldDialog(data) },
        // Per decision #3: routes through the same reason-required Decline
        // flow as the overflow menu's "Decline operation…" (proxy-clicking
        // the existing button, same as that item does) rather than a bare
        // Status flip — no operation can become Declined without a reason
        // and a Dockmaster-sync attempt.
        { label: 'Declined', onClick: () => document.getElementById('deleteButton').click() },
      ]);
    };
  }
}

// ---------- Stage 7b: panel hold banner ----------

function formatMMDD(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d)) return '';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

function renderHoldBanner(data) {
  const slot = document.getElementById('opHoldBannerSlot');
  if (!slot) return;

  if (data.Status !== 'Hold') {
    patchHTML(slot, '');
    return;
  }

  const reason = escapeHtml(data.Hold_Reason || 'On hold');
  const detailParts = [];
  if (data.Hold_Detail) detailParts.push(escapeHtml(data.Hold_Detail));
  if (data.Hold_Follow_Up_Date) detailParts.push(`follow up ${formatMMDD(data.Hold_Follow_Up_Date)}`);

  patchHTML(slot, `
    <div class="op-hold-banner">
      <span class="op-hold-banner-tag">On hold</span>
      <div class="op-hold-banner-text">
        <div class="op-hold-banner-reason">${reason}</div>
        ${detailParts.length ? `<div class="op-hold-banner-detail">${detailParts.join(' · ')}</div>` : ''}
      </div>
      <button type="button" class="op-hold-banner-release" id="opHoldBannerReleaseBtn">Release hold</button>
    </div>
  `);

  document.getElementById('opHoldBannerReleaseBtn').onclick = () => releaseHoldWithToast(data);
}

// ---------- Admin-only "ready to bill" banner ----------
//
// Billing used to be reachable only via the "More" overflow menu (see
// isBillable()/initOverflowMenu() below), which the user reported as
// effectively hidden — an admin had no way to tell an operation needed
// billing without opening it and digging into More. This surfaces the same
// #open-billing action as a prominent banner at the top of the panel,
// admin-only (billing isn't a tech's job) and only while there's actually
// something to do: billable status per opCheck()'s own rule, AND not
// already billed (Billing_Completed is a separate flag from Status — a
// Closed job can be billed or not; billingModal() itself keys its "Billed"
// vs "Ready to bill" status pill off this same field).
function renderBillingBanner(data) {
  const slot = document.getElementById('opBillingBannerSlot');
  if (!slot) return;

  const billableStatus = data.Status === 'Complete' || data.Status === 'Ready to Bill' || data.Status === 'Closed';
  const isAdmin = isAdminRole(localStorage.getItem('currentUserRole') || '');

  if (!billableStatus || data.Billing_Completed || !isAdmin) {
    patchHTML(slot, '');
    return;
  }

  const hours = computeLoggedHours(data);
  const parts = computePartsCount(data);

  patchHTML(slot, `
    <div class="op-billing-banner">
      <span class="op-billing-banner-tag">Ready to bill</span>
      <div class="op-billing-banner-text">
        <div class="op-billing-banner-headline">This operation is ready to bill</div>
        <div class="op-billing-banner-detail">${hours.toFixed(1)} hrs logged &middot; ${parts} part${parts === 1 ? '' : 's'}</div>
      </div>
      <button type="button" class="op-billing-banner-btn" id="opBillingBannerBtn">Bill now &rarr;</button>
    </div>
  `);

  document.getElementById('opBillingBannerBtn').onclick = () => {
    document.getElementById('open-billing').click();
  };
}

// ---------- Stage 5: field-grid footnote (logged hours/parts vs estimate) ----------

function computeLoggedHours(data) {
  if (!data.Labor) return 0;
  return Object.values(data.Labor).reduce((sum, entry) => sum + (Number(entry && entry.Hours) || 0), 0);
}

function computePartsCount(data) {
  if (!data.Parts) return 0;
  return Object.keys(data.Parts).length;
}

function renderFieldGridFootnote(data) {
  const el = document.getElementById('opFieldFootnote');
  if (!el) return;

  const loggedHours = computeLoggedHours(data);
  const partsCount = computePartsCount(data);
  const estHours = Number(data.Estimated_Hours) || 0;
  const percent = estHours > 0 ? Math.round((loggedHours / estHours) * 100) : null;

  let percentHtml = '';
  if (percent !== null) {
    let cls = 'op-field-footnote-pct';
    if (percent > 100) cls += ' is-over';
    else if (percent >= 90) cls += ' is-near';
    percentHtml = `<span class="${cls}">${percent}% of estimate</span>`;
  }

  patchHTML(el, `<span class="op-field-footnote-text">Logged so far `
    + `<span class="op-field-footnote-num">${loggedHours.toFixed(1)} hrs</span> · `
    + `<span class="op-field-footnote-num">${partsCount} parts</span></span>${percentHtml}`);
}

// Phone-only equivalent of the footnote above (Stage 13) — same computed
// values, different presentation. Both are always rendered; which one is
// visible is a pure CSS media-query swap at the 900px breakpoint (avoids
// re-rendering on resize/orientation change).
function renderStatTiles(data) {
  const el = document.getElementById('opStatTiles');
  if (!el) return;

  const loggedHours = computeLoggedHours(data);
  const estHours = data.Estimated_Hours ? Number(data.Estimated_Hours).toFixed(1) : '—';
  const startDate = data.Start_Date ? formatShortDateLocal(data.Start_Date) : '—';

  patchHTML(el, `
    <div class="op-stat-tile"><div class="op-stat-tile-value">${escapeHtml(estHours)}</div><div class="op-stat-tile-label">Est. hrs</div></div>
    <div class="op-stat-tile"><div class="op-stat-tile-value">${loggedHours.toFixed(1)}</div><div class="op-stat-tile-label">Logged</div></div>
    <div class="op-stat-tile"><div class="op-stat-tile-value">${escapeHtml(startDate)}</div><div class="op-stat-tile-label">Starts</div></div>
  `);
}

// ---------- Stage 6: overflow "More" popover ----------
//
// ---------- Stage 10: Duplicate operation ----------
//
// addOperation(info) (app.js) — already wired to the real "Add Operation"
// button — only actually reads Work_Order/Category/Short_Description/
// Customer_ID/Boat_ID off the array it's passed before opening the add-op
// form (confirmed by reading it directly: the form itself only collects
// Category + Short Description before generating/reusing a WO number —
// Op_Type/Estimated_Hours/Charge/Start_Date/Description aren't part of this
// creation step at all, they're filled in afterward on the new op's own
// panel). Duplicate is a prefill into that same real flow, not a silent
// clone — the user still finishes and submits it, and a fresh Job_ID is
// generated internally when they do.
function duplicateOperation(data) {
  addOperation([{
    Work_Order: data.Work_Order,
    Category: data.Category,
    Short_Description: data.Short_Description,
    Customer_ID: data.Customer_ID,
    Boat_ID: data.Boat_ID,
  }]);
}

// ---------- Stage 9: Move to another work order ----------

function formatShortDateLocal(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d)) return '';
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
}

// Mirrors the existing move-to-a-brand-NEW-WO handler's Dockmaster resync
// (app.js's #move-operations-button click handler) — deleteOperationDM()
// off the old WO, updateWorkOrderDM() with the same serviceLog shape onto
// the new one, then updateJobInfo() to actually move the field. Job_ID never
// changes — Work_Order is the only field mutation a move makes.
async function moveOperationToWorkOrder(panelData, newWorkOrder) {
  const data = (await serverOp(panelData.Job_ID)) || panelData;
  if (String(data.Work_Order) !== String(panelData.Work_Order)) {
    toastMessage(`Someone else just moved this operation to ${woLabel(data.Work_Order)}.`, 'blue', 4000);
    refreshWorkOrderRow(panelData.Work_Order);
    if (document.getElementById('currentJobId')?.value === data.Job_ID) opCheck(null, data);
    return false;
  }
  const oldWorkOrder = data.Work_Order;
  const customerData = await getCustomerRecord(data.Customer_ID);
  const userId = localStorage.getItem('currentUserId');
  const clerkId = (globalUserData && globalUserData[userId] && globalUserData[userId].clerkId) || '';

  await deleteOperationDM(oldWorkOrder, data.OpCode);

  const type = (data.Status || '').toLowerCase() === 'internal' ? 'I' : 'R';
  const serviceLog = {
    DMCustomer: customerData?.DM_Customer || '',
    Work_Order: newWorkOrder,
    type,
    LocationCode: '1',
    clerkId,
    Long_Description: data.Description,
    Description: data.Description,
    Short_Description: data.Short_Description_DM || data.Short_Description,
    OpCode: data.OpCode,
  };
  await updateWorkOrderDM(serviceLog);
  await updateJobInfo(data.Job_ID, 'Work_Order', newWorkOrder, { strict: true });

  if (jobsByWorkOrder) {
    if (jobsByWorkOrder.has(oldWorkOrder)) {
      const remaining = jobsByWorkOrder.get(oldWorkOrder).filter((o) => o.Job_ID !== data.Job_ID);
      if (remaining.length) jobsByWorkOrder.set(oldWorkOrder, remaining);
      else jobsByWorkOrder.delete(oldWorkOrder);
    }
    const destArr = jobsByWorkOrder.get(newWorkOrder) || [];
    destArr.push({ ...data, Work_Order: newWorkOrder });
    jobsByWorkOrder.set(newWorkOrder, destArr);
  }

  // History note, same {Note, Tech, Date, Time, Job_ID, id} shape app.js's
  // own note-adding code already uses (e.g. orderPart()'s part-order note).
  const user = await getCurrentUserData();
  const noteId = await generateId();
  const noteDate = await getFormattedDate();
  const noteTime = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const userLabel = (user && (user.displayName || user.email)) || 'Unknown';
  const note = {
    Note: `Moved from ${woLabel(oldWorkOrder)} by ${userLabel} on ${noteDate}`,
    Tech: userLabel,
    Date: noteDate,
    Time: noteTime,
    Job_ID: data.Job_ID,
    id: noteId,
  };
  await addJobNote(data.Job_ID, note);

  refreshWorkOrderRow(oldWorkOrder);
  refreshWorkOrderRow(newWorkOrder);

  opCheck(null, { ...data, Work_Order: newWorkOrder });
}

function openMoveDialog(data) {
  const candidates = getOpenWorkOrdersForBoat(data.Boat_ID, data.Work_Order);
  let selectedWO = null;

  const cardsHtml = candidates.map((c) => `
    <div class="op-check-row" data-wo="${escapeHtml(c.workOrder)}">
      <span class="op-check-row-radio"></span>
      <div class="op-check-row-text">
        <div class="op-check-row-title">${escapeHtml(woLabel(c.workOrder))}</div>
        <div class="op-check-row-sub">Opened ${escapeHtml(formatShortDateLocal(c.openedDate) || '—')} · ${c.opCount} operation${c.opCount === 1 ? '' : 's'} · ${escapeHtml(c.statusSummary)}</div>
      </div>
    </div>
  `).join('');

  const newWoCardHtml = `
    <div class="op-check-row op-check-row--dashed" id="opMoveNewWoCard">
      <span class="op-check-row-radio"></span>
      <div class="op-check-row-text"><div class="op-check-row-title">New work order for this boat…</div></div>
    </div>
  `;

  const billedWarning = Number(data.Labor_Billed) > 0 || Number(data.Parts_Billed) > 0;
  const laborCount = data.Labor ? Object.keys(data.Labor).length : 0;
  const laborHours = computeLoggedHours(data);
  const partsCount = computePartsCount(data);
  const photosCount = data.Photos ? Object.keys(data.Photos).length : 0;
  const notesCount = data.Notes ? Object.keys(data.Notes).length : 0;

  const summaryRows = [
    { ok: true, text: `${laborCount} labor entr${laborCount === 1 ? 'y' : 'ies'} · ${laborHours.toFixed(1)} hrs` },
    { ok: true, text: `${partsCount} part${partsCount === 1 ? '' : 's'}` },
    { ok: true, text: `${photosCount} photo${photosCount === 1 ? '' : 's'}, ${notesCount} note${notesCount === 1 ? '' : 's'}, status and history` },
  ];
  // No per-WO sequence number exists in the real schema (unlike the mock's
  // illustrative "6000-4 → 6014-6" copy) — this warns about context instead.
  summaryRows.push({ ok: false, text: `Op context moves from ${woLabel(data.Work_Order)} to the destination` });
  if (billedWarning) {
    summaryRows.push({ ok: false, text: `Already-billed amount stays attributed to ${woLabel(data.Work_Order)}` });
  }

  const summaryHtml = summaryRows.map((r) => `
    <div class="op-move-summary-row"><span class="op-move-summary-icon ${r.ok ? 'is-ok' : 'is-warn'}">${r.ok ? '✓' : '!'}</span>${escapeHtml(r.text)}</div>
  `).join('');

  const bodyHtml = `
    <div class="op-hold-field">
      <div class="op-hold-detail-label-row">
        <span class="op-rail-fact-label">Move to</span>
        <span class="op-notes-hint">open work orders for this boat</span>
      </div>
      <div class="op-check-row-list" id="opMoveWoList">
        ${cardsHtml}
        ${newWoCardHtml}
      </div>
    </div>
    <div class="op-move-summary">
      <div class="op-move-summary-heading">What moves with it</div>
      ${summaryHtml}
    </div>
  `;

  const footerHtml = `
    <span class="op-dialog-hint">Logged in op history</span>
    <button type="button" class="op-btn-secondary" data-dialog-cancel>Cancel</button>
    <button type="button" class="op-btn-primary op-disabled" id="opMoveSubmitBtn" disabled>Move</button>
  `;

  const scrim = opOpenDialog({
    title: 'Move operation',
    subtitle: `${escapeHtml(data.Short_Description || data.Description || '')} — from ${escapeHtml(woLabel(data.Work_Order || ''))}`,
    bodyHtml,
    footerHtml,
    widthPx: 468,
  });

  scrim.querySelector('[data-dialog-cancel]').addEventListener('click', opCloseDialog);

  const submitBtn = document.getElementById('opMoveSubmitBtn');

  scrim.querySelectorAll('.op-check-row[data-wo]').forEach((card) => {
    card.addEventListener('click', () => {
      selectedWO = card.getAttribute('data-wo');
      scrim.querySelectorAll('.op-check-row').forEach((c) => c.classList.remove('op-check-row--selected'));
      card.classList.add('op-check-row--selected');
      submitBtn.disabled = false;
      submitBtn.classList.remove('op-disabled');
      submitBtn.textContent = `Move to ${selectedWO}`;
    });
  });

  const newWoCard = document.getElementById('opMoveNewWoCard');
  if (newWoCard) {
    newWoCard.addEventListener('click', () => {
      // Per decision #4: bridge into the existing new-WO creation flow
      // rather than reimplementing WO creation inside this new dialog.
      opCloseDialog();
      editWorkOrderData([{
        opCode: data.OpCode,
        description: (data.Description || '').includes(':') ? data.Description.split(':')[0] : data.Description,
        jobId: data.Job_ID,
        workOrder: data.Work_Order,
        data,
      }]);
    });
  }

  submitBtn.addEventListener('click', async () => {
    if (!selectedWO || submitBtn.disabled) return;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Moving…';
    try {
      const moved = await moveOperationToWorkOrder(data, selectedWO);
      opCloseDialog();
      if (moved !== false) toastMessage(`Moved to ${woLabel(selectedWO)}`, 'green');
    } catch (error) {
      console.error('[opPanel] Error moving operation:', error);
      toastMessage('Failed to move operation. Please try again.', 'red');
      submitBtn.disabled = false;
      submitBtn.textContent = `Move to ${selectedWO}`;
    }
  });
}

// #opOverflowMoreBtn is static markup (Stage 5), not recreated per render
// like the status pills are — so its click handler is wired once and must
// read the LATEST operation data at click time, not whatever `data` was in
// scope when the listener was attached. currentOpData is what makes that
// possible; every renderOpPanel() call refreshes it before anything else.
let currentOpData = null;
let overflowMenuInitialized = false;

function isBillable() {
  // Reuse opCheck()'s own computation (app.js ~10675-10681: Status is
  // Complete/Ready to Bill/Closed) via the DOM state it already set, rather
  // than re-deriving the same condition a second time in this file.
  const billingBtn = document.getElementById('open-billing');
  return !!billingBtn && billingBtn.style.display !== 'none';
}

function initOverflowMenu() {
  if (overflowMenuInitialized) return;
  overflowMenuInitialized = true;

  const moreBtn = document.getElementById('opOverflowMoreBtn');
  if (!moreBtn) return;

  moreBtn.addEventListener('click', () => {
    const data = currentOpData;
    if (!data) return;
    const held = data.Status === 'Hold';

    const items = [
      {
        label: held ? 'Release hold' : 'Put on hold',
        onClick: () => (held ? releaseHoldWithToast(data) : openHoldDialog(data)),
      },
    ];

    if (isBillable()) {
      items.push({ label: 'Billing', onClick: () => document.getElementById('open-billing').click() });
    }

    items.push(
      { label: 'Move to another work order', onClick: () => openMoveDialog(data) },
      { label: 'Duplicate operation', onClick: () => duplicateOperation(data) },
      { divider: true },
      {
        label: 'Decline operation…',
        danger: true,
        // Stage 8 swaps this for the consolidated one-dialog flow; for now,
        // proxy-click the existing button so deleteOperation()'s real
        // two-popup flow (unchanged) still runs underneath.
        onClick: () => document.getElementById('deleteButton').click(),
      },
    );

    opOpenMenu(moreBtn, items);
  });
}

// ---------- Stage 11: follow-up prompt (boot-time modal) ----------

function todayISODate() {
  return toISODate(new Date());
}

function daysHeld(startedAt) {
  if (!startedAt) return null;
  const start = new Date(startedAt);
  if (isNaN(start)) return null;
  return Math.max(0, Math.round((Date.now() - start.getTime()) / 86400000));
}

function formatLongDate(d) {
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

// jobsByWorkOrder is the "all currently-loaded ops" source used everywhere
// else in this file (rail switcher, Move dialog) — same reasoning applies
// here: by the time onlineFlow() reaches this call, mainTable() has already
// populated it. A boot-time follow-up check running before any table has
// ever rendered is the one cold-start case those other call sites don't
// have to worry about, which is why the hook point (below) explicitly waits
// on ensureInitialUI()'s promise first.
function getAllLoadedOps() {
  if (!jobsByWorkOrder) return [];
  const all = [];
  jobsByWorkOrder.forEach((ops) => ops.forEach((op) => all.push(op)));
  return all;
}

function computeDueFollowUps() {
  const today = todayISODate();
  return getAllLoadedOps().filter((op) => op.Status === 'Hold' && op.Hold_Follow_Up_Date && op.Hold_Follow_Up_Date <= today);
}

function openFollowUpPrompt(dueOps, ackKey) {
  const modal = document.createElement('div');
  modal.className = 'op-followup-scrim';
  modal.id = 'opFollowUpScrim';

  const handled = new Set();

  const cardsHtml = dueOps.map((op, i) => {
    const days = daysHeld(op.Hold_Started_At);
    // Short_Description is often just the op-code's category name, identical across every
    // operation created from that code (e.g. several distinct "Install Engine" ops on one WO
    // all share it) — two on-hold ops could otherwise render as visually indistinguishable
    // cards. Append the real per-op Description whenever it says something Short_Description
    // doesn't, so staff can tell which specific op they're deciding on.
    const primaryDesc = op.Short_Description || op.Description || '';
    const secondaryDesc = (op.Description && op.Description !== op.Short_Description) ? op.Description : '';
    return `
      <div class="op-followup-card" data-index="${i}">
        <div class="op-followup-card-top">
          <div class="op-followup-card-text">
            <div class="op-followup-card-title">${escapeHtml(op.Boat_Name || '')} · ${escapeHtml(op.Customer_Name || '')}</div>
            <div class="op-followup-card-desc">${escapeHtml(primaryDesc)}${secondaryDesc ? ` — ${escapeHtml(secondaryDesc)}` : ''}</div>
            <div class="op-followup-card-meta">
              <span class="op-followup-hold-days">${days !== null ? `Hold ${days} day${days === 1 ? '' : 's'}` : 'On hold'}</span>
              <span class="op-followup-reason">${escapeHtml(op.Hold_Reason || '')}</span>
            </div>
            ${op.Hold_Detail ? `<div class="op-followup-detail">${escapeHtml(op.Hold_Detail)}</div>` : ''}
          </div>
          <span class="op-followup-wo">${escapeHtml(woLabel(op.Work_Order || ''))}</span>
        </div>
        <div class="op-followup-actions">
          <button type="button" class="op-followup-btn op-followup-btn--primary" data-action="release">Release hold</button>
          <button type="button" class="op-followup-btn" data-action="open">Open op</button>
          <button type="button" class="op-followup-btn" data-action="tomorrow">Tomorrow</button>
          <button type="button" class="op-followup-btn" data-action="pick">Pick a date</button>
          <input type="date" class="op-followup-date-input" style="display:none;" />
        </div>
      </div>
    `;
  }).join('');

  modal.innerHTML = `
    <div class="op-followup-modal">
      <div class="op-followup-head">
        <div class="op-followup-head-row">
          <span class="op-followup-tag">Follow up today</span>
          <span class="op-followup-date">${escapeHtml(formatLongDate(new Date()))}</span>
        </div>
        <div class="op-followup-title">${dueOps.length} held operation${dueOps.length === 1 ? '' : 's'} ${dueOps.length === 1 ? 'is' : 'are'} due for follow-up</div>
        <div class="op-followup-sub">Each one needs a decision before this closes: release the hold, open the op, or push the follow-up to tomorrow or a date you pick.</div>
      </div>
      <div class="op-followup-body">${cardsHtml}</div>
      <div class="op-followup-foot">
        <span class="op-followup-progress" id="opFollowUpProgress">0 of ${dueOps.length} handled</span>
        <button type="button" class="op-btn-secondary" id="opFollowUpPushAll">Push all to tomorrow</button>
        <button type="button" class="op-btn-primary op-disabled" id="opFollowUpDone" disabled>Done</button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  function updateProgress() {
    modal.querySelector('#opFollowUpProgress').textContent = `${handled.size} of ${dueOps.length} handled`;
    const doneBtn = modal.querySelector('#opFollowUpDone');
    const allDone = handled.size === dueOps.length;
    doneBtn.disabled = !allDone;
    doneBtn.classList.toggle('op-disabled', !allDone);
  }

  function markHandled(index) {
    if (handled.has(index)) return;
    handled.add(index);
    const card = modal.querySelector(`.op-followup-card[data-index="${index}"]`);
    if (card) {
      card.classList.add('op-followup-card--handled');
      const actions = card.querySelector('.op-followup-actions');
      if (actions) actions.remove();
    }
    updateProgress();
  }

  dueOps.forEach((op, i) => {
    const card = modal.querySelector(`.op-followup-card[data-index="${i}"]`);
    if (!card) return;
    const dateInput = card.querySelector('.op-followup-date-input');

    card.querySelector('[data-action="release"]').addEventListener('click', async () => {
      try {
        await releaseHoldOperation(op);
        markHandled(i);
      } catch (error) {
        console.error('[opPanel] Error releasing hold from follow-up prompt:', error);
        toastMessage('Failed to release hold. Please try again.', 'red');
      }
    });

    card.querySelector('[data-action="open"]').addEventListener('click', () => {
      modal.remove();
      opCheck(null, op);
    });

    card.querySelector('[data-action="tomorrow"]').addEventListener('click', async () => {
      const tomorrow = toISODate(addDays(new Date(), 1));
      try {
        await updateJobInfo(op.Job_ID, 'Hold_Follow_Up_Date', tomorrow);
        op.Hold_Follow_Up_Date = tomorrow;
        markHandled(i);
      } catch (error) {
        console.error('[opPanel] Error pushing follow-up date:', error);
        toastMessage('Failed to update follow-up date. Please try again.', 'red');
      }
    });

    card.querySelector('[data-action="pick"]').addEventListener('click', () => {
      dateInput.style.display = 'inline-block';
      dateInput.focus();
    });

    dateInput.addEventListener('change', async () => {
      if (!dateInput.value) return;
      try {
        await updateJobInfo(op.Job_ID, 'Hold_Follow_Up_Date', dateInput.value);
        op.Hold_Follow_Up_Date = dateInput.value;
        markHandled(i);
      } catch (error) {
        console.error('[opPanel] Error setting follow-up date:', error);
        toastMessage('Failed to update follow-up date. Please try again.', 'red');
      }
    });
  });

  modal.querySelector('#opFollowUpPushAll').addEventListener('click', async () => {
    const tomorrow = toISODate(addDays(new Date(), 1));
    for (let i = 0; i < dueOps.length; i++) {
      if (handled.has(i)) continue;
      try {
        // eslint-disable-next-line no-await-in-loop
        await updateJobInfo(dueOps[i].Job_ID, 'Hold_Follow_Up_Date', tomorrow);
        markHandled(i);
      } catch (error) {
        console.error('[opPanel] Error in push-all-to-tomorrow:', error);
      }
    }
  });

  modal.querySelector('#opFollowUpDone').addEventListener('click', () => {
    if (handled.size < dueOps.length) return;
    localStorage.setItem(ackKey, '1');
    modal.remove();
  });
}

// Exported and called once from onlineFlow() (app.js), after
// ensureInitialUI()'s promise resolves so jobsByWorkOrder is guaranteed
// populated. "Seen today" is per user per date in localStorage, mirroring
// dashboard.js's TIDE_CACHE_KEY/WEATHER_CACHE_KEY pattern (the closest
// existing precedent) rather than an RTDB field — nothing else needs to
// read this state.
export function checkFollowUpDue() {
  // onlineFlow() (app.js) is triggered from several independent listeners
  // (auth state, browser 'online', Firebase reconnect) and can legitimately
  // fire more than once in a session — guard against stacking a second
  // modal on top of one still awaiting acknowledgement.
  if (document.getElementById('opFollowUpScrim')) return;

  const userId = localStorage.getItem('currentUserId');
  if (!userId) return;
  const ackKey = `followUpAck_${userId}_${todayISODate()}`;
  if (localStorage.getItem(ackKey)) return;

  const due = computeDueFollowUps();
  if (!due.length) return;

  openFollowUpPrompt(due, ackKey);
}

// Live (docs/live-updates-plan.md): another user changed the open operation. Redraws the panel's
// display parts in place (status strip, banners, tiles, rail) — not the fields, and without
// resetting field baselines, so this user's unsaved edits are still saved when they leave a field.
export function refreshOpPanelLive(data) {
  currentOpData = data;
  renderRailSwitcher(data);
  renderHoldBanner(data);
  renderBillingBanner(data);
  renderStatusStrip(data);
  renderFieldGridFootnote(data);
  renderStatTiles(data);
  renderOfficeChecklist(document.getElementById('opChecklistSlot'), data, currentBoat);
  renderOpOrders(document.getElementById('opChecklistSlot'), data);
}

// A paint job says which can, above the checklist.
function renderOpPaint(data, boat) {
  const slot = document.getElementById('opChecklistSlot');
  if (!slot) return;
  let el = document.getElementById('opPaintBanner');
  if (!el) {
    el = document.createElement('div');
    el.id = 'opPaintBanner';
    slot.before(el);
  }
  el.hidden = !isPaintOp(data);
  if (el.hidden) return;
  el.className = `op-paint-banner${paintName(boat) ? '' : ' is-missing'}`;
  const swatch = paintSwatch(boat);
  el.innerHTML = `<span class="op-paint-label">Bottom paint</span><span class="op-paint-name">${swatch ? `<i style="background:${swatch}"></i>` : ''}${escapeHtml(paintLine(boat))}</span>`;
}

export function renderOpPanel(data, customerData) {
  currentOpData = data;
  currentBoat = customerData?.Boats?.[data.Boat_ID]
    || Object.values(customerData?.Boats || {}).find(b => b && b.Boat_ID === data.Boat_ID) || null;
  renderOpPaint(data, currentBoat);
  renderRailSwitcher(data);
  renderHoldBanner(data);
  renderBillingBanner(data);
  renderStatusStrip(data);
  renderFieldGridFootnote(data);
  renderStatTiles(data);
  renderOfficeChecklist(document.getElementById('opChecklistSlot'), data, currentBoat);
  renderOpOrders(document.getElementById('opChecklistSlot'), data);
  initFieldAutosave();
  resetFieldBaselines();
  initOverflowMenu();
}
